import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Loader2 } from 'lucide-react';

export const AuthCallback = () => {
  const navigate = useNavigate();
  const { handleGoogleCallback } = useAuth();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState(null);

  useEffect(() => {
    const processCallback = async () => {
      try {
        // Check for error in query params first (OAuth errors)
        const errorParam = searchParams.get('error');
        const errorDescription = searchParams.get('error_description');
        
        if (errorParam) {
          throw new Error(errorDescription || `OAuth error: ${errorParam}`);
        }
        
        // Extract session_id from URL hash
        const hash = window.location.hash;
        const params = new URLSearchParams(hash.slice(1)); // Remove the #
        const sessionId = params.get('session_id');

        if (!sessionId) {
          // Also check query params as fallback
          const querySessionId = searchParams.get('session_id');
          if (querySessionId) {
            const qData = await handleGoogleCallback(querySessionId);
            navigate(qData?.requires_2fa ? '/login' : '/dashboard', { replace: true, state: qData?.requires_2fa ? { mfaToken: qData.mfa_token } : undefined });
            return;
          }
          throw new Error('No session ID found in callback. Please try logging in again.');
        }

        // Exchange session_id for user data
        const userData = await handleGoogleCallback(sessionId);
        if (userData?.requires_2fa) {
          navigate('/login', { replace: true, state: { mfaToken: userData.mfa_token } });
          return;
        }
        
        // Redirect based on user role
        const hasAdminAccess = userData?.is_admin || userData?.role_id;
        if (hasAdminAccess) {
          navigate('/admin', { replace: true });
        } else {
          navigate('/dashboard', { replace: true });
        }
      } catch (err) {
        console.error('Auth callback error:', err);
        const errorMessage = err.response?.data?.detail?.error_description || err.response?.data?.detail?.message || 
                            err.response?.data?.detail || 
                            err.message || 
                            'Authentication failed';
        setError(errorMessage);
        // Redirect to login after error
        setTimeout(() => navigate('/login', { replace: true }), 3000);
      }
    };

    processCallback();
  }, [handleGoogleCallback, navigate, searchParams]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50">
        <div className="text-center max-w-md px-4">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-red-600 text-2xl">!</span>
          </div>
          <h1 className="text-xl font-semibold text-neutral-900 mb-2">Authentication Failed</h1>
          <p className="text-neutral-500 mb-4">{error}</p>
          <p className="text-sm text-neutral-400">Redirecting to login...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-neutral-50">
      <div className="text-center">
        <Loader2 className="w-12 h-12 text-amber-500 animate-spin mx-auto mb-4" />
        <h1 className="text-xl font-semibold text-neutral-900 mb-2">Signing you in...</h1>
        <p className="text-neutral-500">Please wait while we complete your authentication.</p>
      </div>
    </div>
  );
};

export default AuthCallback;
